/**
 * Working days for board dates: weekdays that are not England and Wales
 * bank holidays. Holidays are computed by rule, so there is no list to keep
 * up to date; a one-off (a coronation, a jubilee) is handled by moving that
 * month's date by hand.
 *
 * Everything works in calendar dates, "YYYY-MM-DD", read and written through
 * UTC so no browser timezone can shift a day.
 */

const pad = (n: number) => String(n).padStart(2, '0')

export function isoDate(year: number, month: number, day: number): string {
  // Date.UTC normalises overflow (day 0, day 32, month 13), which the rules
  // below rely on for "last day of the month" and year rollover.
  const d = new Date(Date.UTC(year, month - 1, day))
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
}

function parts(date: string): [number, number, number] {
  const [y, m, d] = date.split('-').map(Number)
  return [y, m, d]
}

export function addDays(date: string, days: number): string {
  const [y, m, d] = parts(date)
  return isoDate(y, m, d + days)
}

/** 0 = Sunday ... 6 = Saturday. */
export function dayOfWeek(date: string): number {
  const [y, m, d] = parts(date)
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay()
}

const isWeekend = (date: string) => {
  const dow = dayOfWeek(date)
  return dow === 0 || dow === 6
}

/** Easter Sunday by the anonymous Gregorian algorithm (Meeus/Jones/Butcher). */
export function easterSunday(year: number): string {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31)
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return isoDate(year, month, day)
}

/** The first given weekday on or after a date. */
function weekdayOnOrAfter(date: string, dow: number): string {
  return addDays(date, (dow - dayOfWeek(date) + 7) % 7)
}

/** The last given weekday on or before a date. */
function weekdayOnOrBefore(date: string, dow: number): string {
  return addDays(date, -((dayOfWeek(date) - dow + 7) % 7))
}

const MONDAY = 1
const SUBSTITUTE = ' (substitute day)'
const holidayCache = new Map<number, Map<string, string>>()

/**
 * The days England and Wales take off in a year, keyed by the date the day
 * off falls on, named as gov.uk names them. A holiday on a weekend appears
 * only on its substitute weekday, since the weekend is not worked anyway.
 */
export function englandWalesBankHolidays(year: number): Map<string, string> {
  const cached = holidayCache.get(year)
  if (cached) return cached

  const days = new Map<string, string>()

  const newYear = isoDate(year, 1, 1)
  if (isWeekend(newYear)) days.set(weekdayOnOrAfter(newYear, MONDAY), `New Year's Day${SUBSTITUTE}`)
  else days.set(newYear, "New Year's Day")

  const easter = easterSunday(year)
  days.set(addDays(easter, -2), 'Good Friday')
  days.set(addDays(easter, 1), 'Easter Monday')

  days.set(weekdayOnOrAfter(isoDate(year, 5, 1), MONDAY), 'Early May bank holiday')
  days.set(weekdayOnOrBefore(isoDate(year, 5, 31), MONDAY), 'Spring bank holiday')
  days.set(weekdayOnOrBefore(isoDate(year, 8, 31), MONDAY), 'Summer bank holiday')

  const christmas = isoDate(year, 12, 25)
  const boxing = isoDate(year, 12, 26)
  switch (dayOfWeek(christmas)) {
    case 5: // Friday: Boxing Day falls on Saturday.
      days.set(christmas, 'Christmas Day')
      days.set(isoDate(year, 12, 28), `Boxing Day${SUBSTITUTE}`)
      break
    case 6: // Saturday: both move to the following Monday and Tuesday.
      days.set(isoDate(year, 12, 27), `Christmas Day${SUBSTITUTE}`)
      days.set(isoDate(year, 12, 28), `Boxing Day${SUBSTITUTE}`)
      break
    case 0: // Sunday: Boxing Day keeps Monday, Christmas moves to Tuesday.
      days.set(boxing, 'Boxing Day')
      days.set(isoDate(year, 12, 27), `Christmas Day${SUBSTITUTE}`)
      break
    default:
      days.set(christmas, 'Christmas Day')
      days.set(boxing, 'Boxing Day')
  }

  holidayCache.set(year, days)
  return days
}

/** The bank holiday on a date, or undefined. */
export function bankHolidayOn(date: string): string | undefined {
  return englandWalesBankHolidays(parts(date)[0]).get(date)
}

export function isWorkingDay(date: string): boolean {
  return !isWeekend(date) && bankHolidayOn(date) === undefined
}

/** The working day n working days before a date, not counting the date
 *  itself. Zero gives the date back unchanged. */
export function workingDaysBefore(date: string, n: number): string {
  if (!Number.isInteger(n) || n < 0) throw new RangeError(`Working days before must be a whole number from 0, got ${n}`)
  let current = date
  let seen = 0
  while (seen < n) {
    current = addDays(current, -1)
    if (isWorkingDay(current)) seen += 1
  }
  return current
}
